import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminFunctions.css";

const AdminFunctions = ({ functions = [], selectedProject }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");


  // 🔥 Filter visible modules
const visibleFunctions = useMemo(() => {
  return functions
    .filter((f) => f.hidden !== true && f.hidden !== "true")
    .filter((f) => f.name.toLowerCase().includes(search.toLowerCase()));
}, [functions, search]);

  // 🔥 Group by category
  const groupedFunctions = useMemo(() => {
    const result = {};
    visibleFunctions.forEach((f) => {
      const group = f.category || "Other";
      if (!result[group]) result[group] = [];
      result[group].push(f);
    });
    return result;
  }, [visibleFunctions]);

const getCategoryColor = (category) => {
  let hash = 0;

  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;

  // Bright vibrant colors
  return `hsl(${hue}, 85%, 55%)`;
};

  // ========= COUNT FETCHING ========= //
  useEffect(() => {
    const controller = new AbortController();
    const fetchCounts = async () => {
      try {
        const apis = [
          ...new Set(visibleFunctions.map((f) => f.countApi).filter(Boolean)),
        ];

        if (!apis.length) {
          setCounts([]);
          setLoading(false);
          return;
        }

        const responses = await Promise.allSettled(
          apis.map((api) => {
            const url = selectedProject
              ? `${api}${
                  api.includes("?") ? "&" : "?"
                }project=${selectedProject}`
              : api;

            return fetch(url, { signal: controller.signal })
              .then((res) => (res.ok ? res.json() : null))
              .catch(() => null);
          })
        );

        const map = {};
        responses.forEach((r, i) => {
          const api = apis[i];
          if (r.status !== "fulfilled" || !r.value) return;
          const d = r.value;
          map[api] =
            typeof d === "number"
              ? d
              : d.count ??
                d.total ??
                (() => {
                  const firstNum = Object.values(d).find((v) => !isNaN(+v));
                  return +firstNum || null;
                })();
        });

        setCounts(
          visibleFunctions.map((f) =>
            f.countApi ? map[f.countApi] ?? null : null
          )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
    return () => controller.abort();
  }, [visibleFunctions, selectedProject]);

  const formatCount = (n) => (n == null ? "—" : n > 999 ? "999+" : n);

  return (
    <div className="admin-functions-wrapper">
      {/* Search Bar */}
      <div className="admin-search-bar">
        <i className="fas fa-search admin-search-icon" />
        <input
          type="text"
          placeholder="Search modules..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-search-input"
        />
      </div>

      {loading ? (
        <div className="admin-loading">Loading modules…</div>
      ) : (
        Object.entries(groupedFunctions).map(([category, items]) => (
          <section key={category} className="admin-category-section">
            <h3
              className="admin-category-title"
              style={{ "--accent-color": getCategoryColor(category) }}
            >
              {category}
            </h3>

            <div className="admin-functions-container">
              {items.map((func, i) => {
                const countIndex = visibleFunctions.findIndex(
                  (f) => f.name === func.name,
                );

                return (
                  <div
                    key={func.name}
                    className="admin-function-box"
                    onClick={() => navigate(func.link)}
                    role="button"
                  >
                    <div className="admin-function-content">
                      {/* LEFT SIDE */}
                      <div className="admin-function-left">
                        <span className="admin-function-title">
                          {func.name}
                        </span>

                        {counts[countIndex] != null && (
                          <span className="admin-function-count">
                            {formatCount(counts[countIndex])}
                          </span>
                        )}
                      </div>

                      {/* RIGHT SIDE */}
                      <div className="admin-function-right">
                        <div className="admin-function-iconWrapper">
                          {func.icon?.startsWith("fa-") ? (
                            <i
                              className={`fas ${func.icon} admin-function-icon`}
                            />
                          ) : (
                            <img
                              src={func.icon}
                              alt={func.name}
                              className="admin-function-icon"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default AdminFunctions;
